import type { FC } from 'react';
import { SidebarTrigger } from './ui/sidebar';
import { PAGE_HEADER_SURFACE } from './PageHeader';
import { cn } from '@/lib/utils';
import elliePng from '../assets/images/pliable/ellie.png';

/**
 * Ellie's identity header — avatar, greeting, tagline.
 *
 * Shared by the new-conversation page (`pages/Ellie.tsx`) and the conversation
 * detail page (`pages/ellie/ConversationPage.tsx`). It lived as duplicated
 * markup in both and drifted: one had `border-b border` (Tailwind's
 * 1px-all-sides *width* utility, not a colour) which drew a stray left edge, and
 * a raw `<h1>` with no SidebarTrigger, which cost that page its mobile sidebar
 * toggle. One component so it can't diverge a third time.
 *
 * The avatar sits left of a stacked title/tagline, so this can't be `PageHeader`
 * itself — but it wears `PAGE_HEADER_SURFACE`, the same chrome and type scale as
 * every other page header.
 *
 * Not rendered in embedded chat, which is white-labeled — that gate lives at the
 * call site in `pages/Ellie.tsx`.
 */
export const EllieHeader: FC = () => (
  <div
    className={cn(
      'w-full px-6 py-4 flex-shrink-0 z-10 flex items-center gap-3',
      PAGE_HEADER_SURFACE,
    )}
  >
    <SidebarTrigger className="md:hidden h-5 w-5 flex-shrink-0" />
    <img src={elliePng} alt="Ellie" className="h-10 w-10 rounded-full object-cover" />
    <div className="min-w-0">
      <h1 className="text-xl font-semibold text-foreground">Hi, I'm Ellie</h1>
      <p className="text-sm text-muted-foreground">
        I'll pull the numbers, analyze them, and tell you what stands out.
      </p>
    </div>
  </div>
);
